import React from 'react'
import Head from 'next/head'
import Link from 'next/link'
import Pitch from '@src/components/DisposalPage/Pitch'
import RelatedServices from '@src/components/RelatedServices'
import SchemaMarkup from '@src/components/SchemaMarkup'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import classes from '@src/styles/cityPage.module.css'
import type { CityPageLink } from '@src/content/cities'
import type { CityServiceContent, CityServiceSpine } from './types'

const SITE = 'https://getdimension.com'

/** Fixed locale and UTC so the server and browser agree — otherwise hydration warns. */
function formatDate(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export type CityServicePageProps = {
  content: CityServiceContent
  /** Parent service, for breadcrumbs and schema. */
  service: { slug: string; label: string; serviceType: string }
  /** National sections this page inherits — see CityServiceSpine. */
  spine?: CityServiceSpine
  /** The service's other city pages, for the "other cities" links. */
  otherCities?: CityPageLink[]
}

/**
 * Renders one city landing page. Every city page for every service goes through
 * here, so a change to the layout or the markup lands on all of them at once.
 *
 * All the per-city substance lives in the content object — see `types.ts`.
 */
function CityServicePage({ content, service, spine, otherCities }: CityServicePageProps) {
  const url = `${SITE}/${service.slug}/${content.slug}`
  const cityState = `${content.cityName}, ${content.stateCode}`

  // A city may override an inherited section; unset falls through to the parent.
  const approach = content.spineOverrides?.approach ?? spine?.approach
  const caseStudy = content.spineOverrides?.caseStudy ?? spine?.caseStudy

  const serviceData = {
    name: `${service.label} in ${cityState}`,
    description: content.metaDescription,
    serviceType: service.serviceType,
    url,
    areaServed: {
      '@type': 'City',
      name: content.cityName,
      address: {
        '@type': 'PostalAddress',
        addressLocality: content.cityName,
        addressRegion: content.stateCode,
        addressCountry: 'US',
      },
    },
    provider: {
      name: 'Dimension',
      url: SITE,
      address: {
        streetAddress: '2261 Market Street, #4061',
        addressLocality: 'San Francisco',
        addressRegion: 'CA',
        postalCode: '94114',
        addressCountry: 'US',
      },
      telephone: '+1 (415) 340-7182',
    },
  }

  const breadcrumbs = {
    items: [
      { name: 'Home', url: SITE },
      { name: service.label, url: `${SITE}/${service.slug}` },
      { name: cityState, url },
    ],
  }

  const faqData = {
    questions: content.faqs.map((faq) => ({
      question: faq.question,
      answer: faq.answer.join(' '),
    })),
  }

  return (
    <>
      <Head>
        <title>{content.title}</title>
        <meta name="description" content={content.metaDescription} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href={url} />
        <SchemaMarkup type="Service" data={serviceData} />
        <SchemaMarkup type="FAQPage" data={faqData} />
        <SchemaMarkup type="BreadcrumbList" data={breadcrumbs} />
      </Head>

      <div className={classes.root}>
        {/* Rendered from the same `breadcrumbs.items` the BreadcrumbList schema
            above uses, so the markup and what a reader can see cannot drift
            apart — which is what Google's structured data guidance asks for.
            The last item is the current page, so it is text, not a link. */}
        <nav className={classes.breadcrumbs} aria-label="Breadcrumb">
          <ol>
            {breadcrumbs.items.map((item, index) => {
              const isCurrent = index === breadcrumbs.items.length - 1
              return (
                <li key={item.url}>
                  {isCurrent ? (
                    <span aria-current="page">{item.name}</span>
                  ) : (
                    <Link href={new URL(item.url).pathname}>{item.name}</Link>
                  )}
                  {!isCurrent && <span aria-hidden="true">/</span>}
                </li>
              )
            })}
          </ol>
        </nav>

        <Pitch
          variant="hazardous-waste"
          /* This page IS the localized version — its regulator, counties and FAQ
             are all about one city, so a ?location= param from a different city
             must not relabel the headline. See useLocalizedCity. */
          disableCityLocalization
          heading={content.heading}
          material={content.cityName}
          pitchText={content.intro}
          ctaLink={content.ctaLink}
          ctaText={content.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />

        <Clients />

        {/* The local regulatory section. This is the part that is not on the
            national page, so it sits above everything else we could reuse. */}
        <section className={classes.section}>
          <h2 className={classes.sectionHeading}>{content.regulator.heading}</h2>
          <p className={classes.agency}>
            Regulated by{' '}
            {content.regulator.agencyUrl ? (
              <a href={content.regulator.agencyUrl} target="_blank" rel="noopener noreferrer">
                {content.regulator.agency}
              </a>
            ) : (
              content.regulator.agency
            )}
          </p>
          <div className={classes.prose}>
            {content.regulator.body.map((text) => (
              <p key={text}>{text}</p>
            ))}
          </div>

          {content.regulator.requirements && (
            <div className={classes.tableScroll}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th scope="col">Requirement</th>
                    <th scope="col">What it means for your business</th>
                  </tr>
                </thead>
                <tbody>
                  {content.regulator.requirements.map((item) => (
                    <tr key={item.label}>
                      <th scope="row">{item.label}</th>
                      <td>{item.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className={classes.verified}>
            <time dateTime={content.regulator.verifiedOn}>
              {`Regulatory details verified ${formatDate(content.regulator.verifiedOn)}.`}
            </time>{' '}
            Rules change — confirm current requirements with the agency before relying on this
            page.
          </p>
        </section>

        <section className={classes.section}>
          <h2 className={classes.sectionHeading}>Areas we serve around {content.cityName}</h2>
          <div className={classes.prose}>
            <p>{content.areasServed.body}</p>
          </div>
          <ul className={classes.counties}>
            {content.areasServed.counties.map((county) => (
              <li key={county}>{county}</li>
            ))}
          </ul>
        </section>

        <br/>

        {/* The national spine below. A city page should still read as a Dimension
            service page, and these are the sections that do the persuading — the
            process explainer, the sustainability dashboard, a case study. Local
            material stays above them, where it earns the ranking. */}
        <SustainabilityDashboardPromo />

        {caseStudy && <CaseStudy {...caseStudy} />}

        {approach && <Approach {...approach} />}


        <section className={classes.section}>
          <h2 className={classes.sectionHeading}>
            {content.cityName} questions, answered
          </h2>
          <div className={classes.faqs}>
            {content.faqs.map((faq) => (
              <div key={faq.question} className={classes.faq}>
                <h3 className={classes.faqQuestion}>{faq.question}</h3>
                {faq.answer.map((text) => (
                  <p key={text} className={classes.faqAnswer}>
                    {text}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </section>

        <section className={classes.section}>
          <p className={classes.parentLink}>
            Serving a business outside {content.cityName}?{' '}
            <Link href={`/${service.slug}`}>See our nationwide {service.label.toLowerCase()}</Link>.
          </p>
          {/* A plain list of names, not the CityLinks cards: the point is that
              every city page links to every other one, without a block of
              other cities' summaries competing with this page's own topic. */}
          {otherCities && otherCities.length > 0 && (
            <nav aria-label={`${service.label} in other cities`}>
              <h2 className={classes.otherCitiesHeading}>
                {service.label} in other cities
              </h2>
              <ul className={`${classes.counties} ${classes.cityLinks}`}>
                {otherCities.map((city) => (
                  <li key={city.slug}>
                    <Link href={`/${service.slug}/${city.slug}`}>
                      {city.cityName}, {city.stateCode}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </section>

        {/* Already pointed at the city variant of each sibling service that has
            one — see localizeRelatedServices in src/content/cities. The href here
            is not always the one the content file wrote. */}
        <RelatedServices services={content.relatedServices} />
      </div>
    </>
  )
}

export default CityServicePage
